/**
 * Inverting a storage URI back into a file-manager key.
 *
 * `getFileS3Url` (widgets/core/_requests.ts) is the one place a key becomes a URI:
 * `encodeURI('s3://<bucket>/<poolId>/<key>')`. A widget hands that URI back when it asks for a new
 * file, and the dialog wants to open where the current value lives — so this is its exact inverse.
 */

/** The folder to open at, and the key to preselect. Both absent when the URI is not ours. */
export type PickedUriLocation = {
  /** Identity-relative folder, in the `a/b` form `FileTable`'s `initialPath` expects. */
  folder?: string
  /** The full identity-relative key, i.e. `CustomFileData.id`. */
  key?: string
}

export function parsePickedUri(
  uri: string | undefined,
  identityId: string | undefined
): PickedUriLocation {
  if (!uri || !identityId || !uri.startsWith('s3://')) {
    return {}
  }

  let decoded: string
  try {
    // decodeURI, not decodeURIComponent: the URI was built with encodeURI, which never emits %2F,
    // so decodeURIComponent would turn a literal %2F inside a filename into a path separator.
    // Throws URIError on a malformed escape, which is just "not a URI we can use".
    decoded = decodeURI(uri)
  } catch {
    return {}
  }

  const parts = decoded.slice('s3://'.length).split('/')

  // parts[0] is the bucket, deliberately not checked: it differs per environment, and a value saved
  // against an older bucket should still open at the right folder. The pool id *is* checked — a URI
  // belonging to another identity must not drive this user's browser to a path they cannot list.
  if (parts.length < 3 || parts[1] !== identityId) {
    return {}
  }

  const key = parts.slice(2).join('/')
  if (!key || key.endsWith('/')) {
    return {}
  }

  const lastSlash = key.lastIndexOf('/')

  return {folder: lastSlash > 0 ? key.slice(0, lastSlash) : undefined, key}
}
